import { useQuery } from '@tanstack/react-query';
import { FRChartTimeFrames } from 'features/company/constants.types';
import companyApi from 'lib/api/company';
import { errorHandler } from 'utils/errors';
import { Common, color } from '@stockbit/ui';
import If from 'global/Common/If';
import numeral from 'numeral';
import Image from 'global/Image';
import { useFliptBoolean } from 'lib/flipt/hooks';
import { FeatureFlagKeys } from 'lib/flipt/constants/flagKeys';
import { useMemo } from 'react';
import Link from 'next/link';
import useOnClickBuyFR from 'features/company/FRPage/useOnClickBuyFR';
import { ButtonFooter, ChartContainer, CompanySymbol, PopoverContentWrapper, SuspendedBadge } from './styled';
import { EmptyChartContainer, EmptyChartText } from '../../styled';
import Chart from './Chart';

const { Flex, Text, Button, Iconmoon } = Common;

const TIMEFRAME: FRChartTimeFrames = '1y';

interface PopoverContentFRProps {
  symbol: string;
  isFollowed: boolean;
  handleFollowCompany: () => void;
}

const PopoverContentFR = ({
  symbol,
  isFollowed,
  handleFollowCompany,
}: PopoverContentFRProps) => {
  const isFREligible = useFliptBoolean(FeatureFlagKeys.FR_BONDS);

  const onBuyBottonClick = useOnClickBuyFR(symbol);

  const {
    data: companyData,
  } = useQuery({
    queryKey: ['company-info-data', symbol],
    queryFn: async () => {
      try {
        const response = await companyApi.getCompanyInfoV2(symbol, 'IDX');

        if (!response.data) {
          throw new Error(
            'Something is terribly wrong, please contact our administrator',
          );
        }

        const { data } = response;

        return data.data;
      } catch (error) {
        errorHandler(error, 'Failed to get detail company');
        return undefined;
      }
    },
  });

  const {
    data: finItem,
  } = useQuery({
    queryKey: ['company-fin-data', symbol],
    queryFn: async () => {
      try {
        const response = await companyApi.getCompanyFinItem(symbol, 'IDX');

        if (!response.data) {
          throw new Error(
            'Something is terribly wrong, please contact our administrator',
          );
        }

        const { data } = response;

        // FRBondsNavigateTrackers.onBondProductPageViewed(symbol, data.data.fr_fitem.yield);

        return data.data.fr_fitem;
      } catch (error) {
        errorHandler(error, 'Failed to detail company');
        return undefined;
      }
    },
    retry: false,
  });

  const {
    data: chartData,
  } = useQuery({
    queryKey: ['company-chart-data', symbol],
    queryFn: async () => {
      try {
        const response = await companyApi.getCompanyChartV2(symbol, TIMEFRAME);

        if (!response.data) {
          throw new Error(
            'Something is terribly wrong, please contact our administrator',
          );
        }

        const responseData = response.data?.data;

        return responseData;
      } catch (error) {
        errorHandler(error, 'Failed to get detail company chart');
        return undefined;
      }
    },
    retry: false,
    initialData: () => ({
      chart_points: [],
    }),
  });

  const isMatured = useMemo(() => {
    if (companyData) {
      if (companyData.tags.length > 0 && companyData.tags.some((item) => item.name === 'MATURED')) {
        return true;
      }
    }
    return false;
  }, [companyData]);

  const isTradable = useMemo(() => {
    if (companyData) {
      return companyData.tradable === 1;
    }
    return false;
  }, [companyData]);

  return (
    <PopoverContentWrapper>
      {isMatured && (
        <SuspendedBadge>
          <Text as="span" size="12px" color="inherit">
            Matured
          </Text>
        </SuspendedBadge>
      )}
      <ChartContainer>
        <If condition={!!chartData}>
          <Chart
            chart={chartData}
            chartType="yield"
          />
          <If.Else>
            <EmptyChartContainer>
              <Image src="/illustration/chart-unavailable.svg" alt="Empty Chart" />
              <EmptyChartText>Data Chart Belum Tersedia</EmptyChartText>
            </EmptyChartContainer>
          </If.Else>
        </If>
      </ChartContainer>
      <Flex justify="space-between" align="center" padding="10px">
        <Flex align="center">
          <CompanySymbol>
            <Text
              color={color.light}
              family="bold"
              size="18px"
              padding="3px 20px 0 8px"
            >
              {symbol}
            </Text>
          </CompanySymbol>
          <Text ml="12px" size="13px" color="#858585">
            Yield
          </Text>
          <Text family="bold" ml="6px">
            {numeral(finItem?.yield).format('0.00') || ''}%
          </Text>
        </Flex>
        <Flex>
          <Button
            height="30px"
            padding="0 8px"
            margin="0 5px"
            round
            onClick={handleFollowCompany}
          >
            <Iconmoon
              className={
                isFollowed
                  ? 'icon-toolbar_check'
                  : 'icon-toolbar_plus'
              }
              size="14px"
              color={color.primary}
            />
          </Button>
          <If condition={isTradable && isFREligible}>
            <Button
              data-cy="stream-popover-company-buy"
              type="primary"
              height="30px"
              padding="0 25px"
              round
              onClick={onBuyBottonClick}
            >
              <Text color={color.light} align="center">
                Buy
              </Text>
            </Button>
          </If>
        </Flex>
      </Flex>
      <Link href={`/symbol/${symbol}`} passHref>
        <ButtonFooter>
          <Text
            as="span"
            weight="bold"
          >
            Tentang Obligasi {symbol}
          </Text>
          <Image
            src="icon/chevron-right-gray.svg"
            width="18"
            height="18"
          />
        </ButtonFooter>
      </Link>
    </PopoverContentWrapper>
  );
};

export default PopoverContentFR;
